import React, { useEffect, useRef, useState } from 'react'
import { Card, Form, Button, Row, Col, Spin, Table, Select, Modal } from 'antd'
import { FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { wtApiCall } from 'src/helpers/commons/ApiWrapper'
import { currentDateTime, wtFormatDate } from 'src/helpers/commons/moment'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtFormatRoundedValue, wtRoundValue } from 'src/helpers/commons/utils'
import WidgetDatePicker from 'src/widgets/DatePickers/WidgetDatePicker'
import moment from 'moment'
import WidgetWarehouseSelector from 'src/widgets/Selectors/WidgetWarehouseSelector'
import BaseListing from 'src/BaseListings'
import { useDownloadExcel } from 'react-export-table-to-excel'
import WidgetItemSelector from 'src/widgets/Selectors/WidgetItemSelector'
import ReportVersionTabs from '../StockAgingReport/ReportVersionTabs'
import '../StockAgingReport/stockAgingReport.css'

const normalizeWarehouseLocationIds = (value) => {
  if (Array.isArray(value)) {
    return value.map((id) => +id).filter((id) => id > 0)
  }

  if (value === undefined || value === null || value === '') {
    return []
  }

  return String(value)
    .split(',')
    .map((id) => +id)
    .filter((id) => id > 0)
}

const getWarehouseOptionValue = (warehouse) => +warehouse?.value || +warehouse?.id || 0

const getReferenceLabels = (references = []) =>
  references.map((reference) => reference?.reference_label).filter(Boolean).join(', ')

const authUser = wtGetFromLocalStorage('authUser')

const isTotalWarehouseLabel = (value) => String(value || '').trim().toUpperCase() === 'TOTAL'

const normalizeRowType = (value) => String(value || '').trim().toLowerCase().replace(/_/g, '-')

const getRowType = (row) => {
  const normalizedRowType = normalizeRowType(row?.row_type)

  if (normalizedRowType === 'grand-total') {
    return 'grand-total'
  }

  if (normalizedRowType === 'brand-total') {
    return 'brand-total'
  }

  return isTotalWarehouseLabel(row?.warehouse) ? 'grand-total' : 'detail'
}

const isDetailRow = (row) => getRowType(row) === 'detail'

const isGrandTotalRow = (row) => getRowType(row) === 'grand-total'

const normalizeBrandName = (row) => String(row?.brand_name || '').trim() || 'Unbranded'

const toNumber = (value) => +value || 0

const compareText = (firstValue, secondValue) =>
  String(firstValue || '').localeCompare(String(secondValue || ''), undefined, {
    sensitivity: 'base',
    numeric: true,
  })

const sortRowsByBrandWithTotals = (rows = []) => {
  const grandTotalRows = []
  const detailRows = []
  const brandTotals = new Map()

  rows.forEach((row, index) => {
    const normalizedRow = {
      ...row,
      row_type: isGrandTotalRow(row) ? 'grand-total' : 'detail',
      original_index: index,
    }

    if (normalizedRow.row_type === 'grand-total') {
      grandTotalRows.push({
        ...normalizedRow,
        warehouse: 'Grand Total',
        item_code: '',
        item_name: '',
        brand_name: '',
        unit_name: '',
      })
      return
    }

    const brandName = normalizeBrandName(normalizedRow)
    brandTotals.set(brandName, toNumber(brandTotals.get(brandName)) + toNumber(normalizedRow.value))
    detailRows.push(normalizedRow)
  })

  detailRows.sort((firstRow, secondRow) => {
    const firstBrandName = normalizeBrandName(firstRow)
    const secondBrandName = normalizeBrandName(secondRow)
    const firstBrandTotal = toNumber(brandTotals.get(firstBrandName))
    const secondBrandTotal = toNumber(brandTotals.get(secondBrandName))
    const brandTotalComparison = secondBrandTotal - firstBrandTotal
    if (brandTotalComparison !== 0) return brandTotalComparison

    const brandComparison = compareText(firstBrandName, secondBrandName)
    if (brandComparison !== 0) return brandComparison

    const warehouseComparison = compareText(firstRow.warehouse, secondRow.warehouse)
    if (warehouseComparison !== 0) return warehouseComparison

    const itemNameComparison = compareText(firstRow.item_name, secondRow.item_name)
    if (itemNameComparison !== 0) return itemNameComparison

    const itemCodeComparison = compareText(firstRow.item_code, secondRow.item_code)
    if (itemCodeComparison !== 0) return itemCodeComparison

    const unitComparison = compareText(firstRow.unit_name, secondRow.unit_name)
    if (unitComparison !== 0) return unitComparison

    const daysComparison = toNumber(firstRow.days_in_inventory) - toNumber(secondRow.days_in_inventory)
    if (daysComparison !== 0) return daysComparison

    return toNumber(firstRow.original_index) - toNumber(secondRow.original_index)
  })

  const rowsWithTotals = []
  let activeBrandName = ''
  let brandQtyTotal = 0
  let brandValueTotal = 0
  let brandQtyDaysTotal = 0

  const appendBrandTotalRow = () => {
    if (!activeBrandName || activeBrandName === 'Unbranded') {
      return
    }

    rowsWithTotals.push({
      row_type: 'brand-total',
      brand_label: activeBrandName,
      warehouse: '',
      item_code: '',
      item_name: `${activeBrandName} Total`,
      brand_name: '',
      unit_name: '',
      qty: brandQtyTotal,
      value: brandValueTotal,
      days_in_inventory: null,
      total_qty: brandQtyTotal,
      total_value: brandValueTotal,
      average_days: brandQtyTotal > 0 ? brandQtyDaysTotal / brandQtyTotal : 0,
      detail_breakdown: [],
    })
  }

  detailRows.forEach((row) => {
    const brandName = normalizeBrandName(row)

    if (activeBrandName && brandName !== activeBrandName) {
      appendBrandTotalRow()
      brandQtyTotal = 0
      brandValueTotal = 0
      brandQtyDaysTotal = 0
    }

    activeBrandName = brandName
    row.brand_name = brandName
    if (brandName !== 'Unbranded' && rowsWithTotals[rowsWithTotals.length - 1]?.row_type !== 'brand-header') {
      rowsWithTotals.push({
        row_type: 'brand-header',
        brand_label: brandName,
        warehouse: '',
        item_code: '',
        item_name: brandName,
        brand_name: '',
        unit_name: '',
        qty: null,
        value: null,
        days_in_inventory: null,
        total_qty: null,
        total_value: null,
        average_days: null,
        detail_breakdown: [],
      })
    }

    brandQtyTotal += toNumber(row.qty)
    brandValueTotal += toNumber(row.value)
    brandQtyDaysTotal += toNumber(row.qty) * toNumber(row.days_in_inventory)
    rowsWithTotals.push(row)
  })

  appendBrandTotalRow()

  return [...rowsWithTotals, ...grandTotalRows]
}

const withMergedTotalColumns = (rows) => {
  const normalizedRows = (rows || []).map((row) => ({
    ...row,
    item_name_row_span: 1,
    unit_name_row_span: 1,
    total_qty_row_span: 1,
    total_value_row_span: 1,
    average_days_row_span: 1,
  }))

  let startIndex = 0
  let colorGroupIndex = 0

  while (startIndex < normalizedRows.length) {
    const currentRow = normalizedRows[startIndex]

    if (!currentRow || !isDetailRow(currentRow)) {
      startIndex += 1
      continue
    }

    const groupKey = [
      currentRow.warehouse,
      currentRow.item_name,
      currentRow.brand_name,
      currentRow.unit_name,
      currentRow.total_qty,
      currentRow.total_value,
      currentRow.average_days,
    ].join('|')

    let endIndex = startIndex + 1

    while (endIndex < normalizedRows.length) {
      const nextRow = normalizedRows[endIndex]

      if (!nextRow || !isDetailRow(nextRow)) {
        break
      }

      const nextGroupKey = [
        nextRow.warehouse,
        nextRow.item_name,
        nextRow.brand_name,
        nextRow.unit_name,
        nextRow.total_qty,
        nextRow.total_value,
        nextRow.average_days,
      ].join('|')

      if (nextGroupKey !== groupKey) {
        break
      }

      endIndex += 1
    }

        const rowSpan = endIndex - startIndex
        const colorClassName = `stock-aging-v2-row-group-${colorGroupIndex % 4}`

    normalizedRows[startIndex].item_name_row_span = rowSpan
    normalizedRows[startIndex].unit_name_row_span = rowSpan
    normalizedRows[startIndex].total_qty_row_span = rowSpan
    normalizedRows[startIndex].total_value_row_span = rowSpan
    normalizedRows[startIndex].average_days_row_span = rowSpan
    normalizedRows[startIndex].row_color_class = colorClassName

    for (let index = startIndex + 1; index < endIndex; index += 1) {
      normalizedRows[index].item_name_row_span = 0
      normalizedRows[index].unit_name_row_span = 0
      normalizedRows[index].total_qty_row_span = 0
      normalizedRows[index].total_value_row_span = 0
      normalizedRows[index].average_days_row_span = 0
      normalizedRows[index].row_color_class = colorClassName
    }

    colorGroupIndex += 1
    startIndex = endIndex
  }

  return normalizedRows
}

const getGrandTotalLabelCellProps = (record, columnIndex) => {
  if (record?.row_type !== 'grand-total') {
    return {}
  }

  if (columnIndex === 0) {
    return {
      colSpan: 5,
      style: {
        textAlign: 'right',
        fontWeight: 'bold',
      },
    }
  }

  if (columnIndex >= 1 && columnIndex <= 4) {
    return {
      colSpan: 0,
    }
  }

  return {}
}

const StockAgingReportV2 = ({ isEmbedded = false }) => {
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [dateRange, setDateRange] = useState(moment().format('YYYY-MM-DD'))
  const [stockData, setStockData] = useState([])
  const [allWarehouses, setAllWarehouses] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [WarehouseLocations, setWarehouseLocations] = useState([])
  const [WarehouseLocationIds, setWarehouseLocationIds] = useState([])
  const [Items, setItems] = useState([])
  const [WarehouseName, setWarehouseName] = useState('')
  const [WarehouseId, setWarehouseId] = useState(0)
  const [ItemName, setItemName] = useState('')
  const [ItemId, setItemId] = useState(0)
  const [selectedInventoryRow, setSelectedInventoryRow] = useState(null)
  const [detailsModalType, setDetailsModalType] = useState('days')
  const Base = new BaseListing('stock_report')
  const tableRef = useRef(null)

  const companyName = wtGetFromLocalStorage('company_name')

  const syncSelectedWarehouse = (nextWarehouses) => {
    const selectedWarehouseStillAllowed = (nextWarehouses || []).some(
      (warehouse) => getWarehouseOptionValue(warehouse) === +WarehouseId,
    )

    if (WarehouseId && !selectedWarehouseStillAllowed) {
      setWarehouseId(0)
      setWarehouseName('')
    }
  }

  const loadWarehousesForLocations = (warehouseLocationIds, fallbackWarehouses = allWarehouses) => {
    const normalizedLocationIds = normalizeWarehouseLocationIds(warehouseLocationIds)

    if (normalizedLocationIds.length === 0) {
      setWarehouses(fallbackWarehouses || [])
      syncSelectedWarehouse(fallbackWarehouses || [])
      return
    }

    Promise.all(
      normalizedLocationIds.map(
        (warehouseLocationId) =>
          new Promise((resolve, reject) => {
            wtApiCall(
              'dropdown',
              {
                dropdown_names: 'warehouses',
                warehouse_location_id: warehouseLocationId,
              },
              'get',
              (result) => {
                resolve(result?.data?.[0]?.warehouses || [])
              },
              (error) => reject(error),
            )
          }),
      ),
    )
      .then((results) => {
        const mergedWarehouses = []
        const seenWarehouseIds = new Set()

        results.flat().forEach((warehouse) => {
          const warehouseId = getWarehouseOptionValue(warehouse)
          if (!warehouseId || seenWarehouseIds.has(warehouseId)) {
            return
          }

          seenWarehouseIds.add(warehouseId)
          mergedWarehouses.push(warehouse)
        })

        setWarehouses(mergedWarehouses)
        syncSelectedWarehouse(mergedWarehouses)
      })
      .catch((error) => {
        lzNotifyErrorMessageFromResult(error)
        setWarehouses([])
        syncSelectedWarehouse([])
      })
  }

  const changeValues = (key, val) => {
    if (key === 'warehouse_id') {
      setWarehouseId(val.value)
      setWarehouseName(val.label)
    }
    if (key === 'item_id') {
      setItemId(val.value)
      setItemName(val.label)
    }
    if (key === 'warehouse_location_ids') {
      const nextLocationIds = val || []
      setWarehouseLocationIds(nextLocationIds)
      loadWarehousesForLocations(nextLocationIds)
    }
  }

  const fetchStockAgingRows = (params) =>
    new Promise((resolve, reject) => {
      wtApiCall(
        'reports/stock_aging_report_v2',
        params,
        'get',
        (response) => {
          if (Array.isArray(response?.data?.data)) {
            resolve(response.data.data)
            return
          }

          resolve(Array.isArray(response?.data) ? response.data : [])
        },
        (error) => {
          reject(error)
        },
      )
    })

  const openDaysInInventoryDetails = (record) => {
    if (!isDetailRow(record) || record.days_in_inventory === null || record.days_in_inventory === undefined) {
      return
    }

    setDetailsModalType('days')
    setSelectedInventoryRow(record)
  }

  const openAverageDaysDetails = (record) => {
    if (!isDetailRow(record) || record.average_days === null || record.average_days === undefined) {
      return
    }

    setDetailsModalType('average')
    setSelectedInventoryRow(record)
  }

  const closeInventoryDetails = () => {
    setSelectedInventoryRow(null)
    setDetailsModalType('days')
  }

  const averageDaysBreakdown = selectedInventoryRow
    ? stockData.filter(
        (row) =>
          isDetailRow(row) &&
          row.warehouse === selectedInventoryRow.warehouse &&
          row.item_code === selectedInventoryRow.item_code &&
          row.item_name === selectedInventoryRow.item_name &&
          row.brand_name === selectedInventoryRow.brand_name &&
          row.unit_name === selectedInventoryRow.unit_name,
      )
    : []

  const tableData = stockData
    .filter((row) => row.row_type !== 'brand-header')
    .map((row) =>
      row.row_type === 'brand-total'
        ? {
            ...row,
            item_name: 'Brand Total',
            brand_name: row.brand_label || '',
          }
        : row,
    )

  const columns = [
    {
      title: 'Warehouse',
      dataIndex: ['warehouse'],
      key: 'warehouse',
      onCell: (record) => ({
        rowSpan: 1,
        ...getGrandTotalLabelCellProps(record, 0),
      }),
    },
    {
      title: 'Item Code',
      dataIndex: ['item_code'],
      key: 'item_code',
      onCell: (record) => ({
        rowSpan: 1,
        ...getGrandTotalLabelCellProps(record, 1),
      }),
    },
    {
      title: 'Item Name',
      dataIndex: ['item_name'],
      key: 'item_name',
      onCell: (record) => ({
        rowSpan: record.item_name_row_span,
        ...getGrandTotalLabelCellProps(record, 2),
      }),
    },
    {
      title: 'Brand',
      dataIndex: ['brand_name'],
      key: 'brand_name',
      onCell: (record) => ({
        rowSpan: 1,
        ...getGrandTotalLabelCellProps(record, 3),
      }),
    },
    {
      title: 'Unit',
      dataIndex: ['unit_name'],
      key: 'unit_name',
      onCell: (record) => ({
        rowSpan: record.unit_name_row_span,
        ...getGrandTotalLabelCellProps(record, 4),
      }),
    },
    {
      title: 'Qty',
      dataIndex: 'qty',
      key: 'qty',
      align: 'right',
      render: (value) => (value === null || value === undefined ? '' : wtFormatRoundedValue(value, '', false, 0)),
    },
    {
      title: 'Values',
      dataIndex: 'value',
      key: 'value',
      align: 'right',
      render: (value) => (value === null || value === undefined ? '' : wtFormatRoundedValue(value, '', false, 3)),
    },
    {
      title: 'Days in Inventory',
      dataIndex: 'days_in_inventory',
      key: 'days_in_inventory',
      align: 'right',
      render: (value, record) => {
        if (value === null || value === undefined) {
          return ''
        }

        return (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto' }}
            onClick={() => openDaysInInventoryDetails(record)}
          >
            {wtRoundValue(value, 0)}
          </Button>
        )
      },
    },
    {
      title: 'Total Qty',
      dataIndex: 'total_qty',
      key: 'total_qty',
      align: 'right',
      onCell: (record) => ({
        rowSpan: record.row_type === 'brand-total' ? 1 : record.total_qty_row_span,
      }),
      render: (value) =>
        value === null || value === undefined ? '' : wtFormatRoundedValue(value, '', false, 0),
    },
    {
      title: 'Total Value',
      dataIndex: 'total_value',
      key: 'total_value',
      align: 'right',
      onCell: (record) => ({
        rowSpan: record.row_type === 'brand-total' ? 1 : record.total_value_row_span,
      }),
      render: (value) => (value === null || value === undefined ? '' : wtFormatRoundedValue(value, '', false, 3)),
    },
    {
      title: 'Average Days',
      dataIndex: 'average_days',
      key: 'average_days',
      align: 'right',
      onCell: (record) => ({
        rowSpan: record.row_type === 'brand-total' ? 1 : record.average_days_row_span,
      }),
      render: (value, record) => {
        if (value === null || value === undefined) {
          return ''
        }

        if (record?.row_type === 'brand-total') {
          return wtRoundValue(value, 0)
        }

        return (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto' }}
            onClick={() => openAverageDaysDetails(record)}
          >
            {wtRoundValue(value, 0)}
          </Button>
        )
      },
    },
  ]

  const getReportData = (overrideFilters = null) => {
    const filters =
      overrideFilters || {
        date: dateRange,
        warehouse_id: WarehouseId,
        item_id: ItemId,
        warehouse_location_ids: WarehouseLocationIds.join(',') || 0,
      }
    const normalizedWarehouseLocationIds = normalizeWarehouseLocationIds(
      filters.warehouse_location_ids,
    )

    setIsLoading(true)
    const baseParams = {
      date: filters.date,
      item_id: filters.item_id,
      warehouse_location_ids: normalizedWarehouseLocationIds.join(',') || 0,
    }

    fetchStockAgingRows({
      ...baseParams,
      warehouse_id: +filters.warehouse_id || 0,
    })
      .then((rows) => {
        setStockData(withMergedTotalColumns(sortRowsByBrandWithTotals(rows)))
        setIsLoading(false)
      })
      .catch((error) => {
        lzNotifyErrorMessageFromResult(error)
        setStockData([])
        setIsLoading(false)
      })
  }

  const handleGenerateReport = () => {
    const normalizedWarehouseLocationIds = normalizeWarehouseLocationIds(WarehouseLocationIds)

    if (normalizedWarehouseLocationIds.length === 0) {
      lzNotifyErrorMessageFromResult('Warehouse Location is required')
      return
    }

    getReportData({
      date: dateRange,
      warehouse_id: WarehouseId,
      item_id: ItemId,
      warehouse_location_ids: normalizedWarehouseLocationIds,
    })
  }

  const { onDownload } = useDownloadExcel({
    currentTableRef: tableRef.current,
    filename: 'Stock Aging Report V2',
    sheet: 'Sheet',
  })

  const generatePDF = () => {
    const summaryOrientation = 'portrait'
    const detailOrientation = 'landscape'
    const pageFormat = 'a4'
    const doc = new jsPDF({ orientation: summaryOrientation, unit: 'mm', format: 'a4' })
    const totalPagesExp = '{total_pages_count_string}'
    const logo = 'assets/wil-icon.png'

    const formatIntegerCell = (value) =>
      value !== null && value !== undefined ? wtFormatRoundedValue(value, '', false, 0) : ''

    const formatRoundedCell = (value) =>
      value !== null && value !== undefined ? wtRoundValue(value, 0) : ''

    const formatValueCell = (value) =>
      value !== null && value !== undefined ? wtFormatRoundedValue(value, '', false, 3) : ''

    const getMergedNumberCell = (content, rowSpan, extraStyles = {}) =>
      rowSpan === 0
        ? ''
        : {
            content,
            rowSpan,
            styles: {
              halign: 'right',
              valign: 'middle',
              ...extraStyles,
            },
          }

    const header = () => {
      const pageSize = doc.internal.pageSize
      const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth()

      doc.setFontSize(11)
      doc.setTextColor('#3c4b64')
      doc.setFont('Helvetica', 'bold')
      try {
        doc.addImage(logo, 'PNG', 5, 4, 25, 25)
      } catch (error) {
        // Keep PDF generation working if logo is unavailable.
      }
      doc.line(32, 6, 32, 25)

      doc.text('WAH', 35, 10, { align: 'left' })
      doc.text('INDUSTRIES', 35, 15)
      doc.text('LIMITED', 35, 20)
      doc.setFontSize(9)
      doc.text('www.wahindustries.com', 35, 25)
      doc.setFontSize(11)
      doc.text('STOCK AGING REPORT V2', pageWidth - 92, 15, { align: 'left' })
      doc.text('AS OF ' + wtFormatDate(dateRange, 'MMMM D, Y'), pageWidth - 92, 22, { align: 'left' })
      doc.setTextColor('black')
      doc.text('Warehouse: ', pageWidth - 92, 28, { align: 'left' })
      doc.setTextColor('#0b3d91')
      doc.text(WarehouseName || 'All', pageWidth - 70, 28, { align: 'left' })

      doc.line(10, 33, pageWidth - 10, 33)
    }

    const footer = () => {
      const pageNo = doc.internal.getNumberOfPages()
      const pageSize = doc.internal.pageSize
      const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight()
      const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth()

      let str = 'Page ' + pageNo
      if (typeof doc.putTotalPages === 'function') {
        str = str + ' of ' + totalPagesExp
      }

      doc.setTextColor('black')
      doc.setFontSize(9)
      doc.line(14, pageHeight - 12, pageWidth - 14, pageHeight - 12)
      doc.text(str, pageWidth - 12, pageHeight - 8, { align: 'right' })
      doc.text('Printed By ', 20, pageHeight - 8, { align: 'left' })
      doc.setTextColor('blue')
      doc.text(`${authUser ? authUser.name : ''}`, 37, pageHeight - 8, { align: 'left' })
    }

    const tableHead = [[
        'Item Name',
        'Unit',
        'Qty',
        'Value',
        'Days in\nInventory',
        'Total Qty',
        'Total Value',
        'Average Days',
      ]]

    const tableOptions = {
      theme: 'plain',
      styles: {
        fontSize: 9,
        cellPadding: 1.5,
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [11, 61, 145],
        fontStyle: 'bold',
        fontSize: 9,
        valign: 'middle',
      },
      columnStyles: {
        0: { cellWidth: 92 },
        1: { cellWidth: 16 },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right' },
        7: { halign: 'right' },
      },
      margin: { top: 40, bottom: 15, left: 10, right: 10 },
      didDrawPage: () => {
        header()
        footer()
      },
    }

    const buildPdfDetailRow = (row, nextRow) => ({
      rowType: 'detail',
      isItemGroupEnd: !nextRow || (nextRow.pdf_item_name_row_span || 0) > 0,
      itemGroupRowSpan: row.pdf_item_name_row_span || 1,
      itemNameText: row.pdf_item_name_row_span === 0 ? '' : row.item_name || '',
      unitText: row.pdf_unit_name_row_span === 0 ? '' : row.unit_name || '',
      cells: [
        row.pdf_item_name_row_span === 0 ? '' : ' ',
        row.pdf_unit_name_row_span === 0 ? '' : ' ',
        formatIntegerCell(row.qty),
        formatValueCell(row.value),
        formatRoundedCell(row.days_in_inventory),
        getMergedNumberCell(formatIntegerCell(row.total_qty), row.pdf_total_qty_row_span),
        getMergedNumberCell(formatValueCell(row.total_value), row.pdf_total_value_row_span, {
          halign: 'center',
        }),
        getMergedNumberCell(formatRoundedCell(row.average_days), row.pdf_average_days_row_span, {
          halign: 'center',
        }),
      ],
    })

    const buildPdfBrandTotalRow = (row) => ({
      rowType: 'brand-total',
      isItemGroupEnd: true,
      itemNameText: '',
      cells: [
        {
          content: `${row.brand_label || ''} Total`,
          colSpan: 2,
          styles: {
            halign: 'right',
            fontStyle: 'bold',
          },
        },
        {
          content: formatIntegerCell(row.qty ?? row.total_qty),
          styles: { halign: 'right', cellPadding: 1.5 },
        },
        {
          content: formatValueCell(row.value ?? row.total_value),
          styles: { halign: 'right', cellPadding: 1.5 },
        },
        {
          content: '',
          styles: { cellPadding: 1.5 },
        },
        {
          content: formatIntegerCell(row.total_qty ?? row.qty),
          styles: { halign: 'right', cellPadding: 1.5 },
        },
        {
          content: formatValueCell(row.total_value ?? row.value),
          styles: { halign: 'center', cellPadding: 1.5 },
        },
        {
          content: formatRoundedCell(row.average_days),
          styles: { halign: 'center', cellPadding: 1.5 },
        },
      ],
    })

    const buildPdfGrandTotalRow = (row) => ({
      rowType: 'grand-total',
      isItemGroupEnd: false,
      itemNameText: '',
      cells: [
        {
          content: 'Grand Total',
          colSpan: 3,
          styles: {
            halign: 'right',
            fontStyle: 'bold',
          },
        },
        formatIntegerCell(row.qty),
        formatValueCell(row.value),
        formatRoundedCell(row.days_in_inventory),
        formatIntegerCell(row.total_qty),
        formatValueCell(row.total_value),
        formatRoundedCell(row.average_days),
      ],
    })

    const buildPdfSummaryRows = (sections, grandTotal) => {
      const rows = sections.map((section, index) => {
        const totalRow = section.totalRow || {}

        return [
          String(index + 1),
          section.brandLabel,
          formatValueCell(totalRow.total_value ?? totalRow.value),
          formatRoundedCell(totalRow.average_days),
        ]
      })

      if (grandTotal) {
        rows.push([
          '',
          'Grand Total',
          formatValueCell(grandTotal.value),
          '',
        ])
      }

      return rows
    }

    const withPdfMergedRows = (rows = []) => {
      const normalizedRows = rows.map((row) => ({
        ...row,
        pdf_item_name_row_span: 1,
        pdf_unit_name_row_span: 1,
        pdf_total_qty_row_span: 1,
        pdf_total_value_row_span: 1,
        pdf_average_days_row_span: 1,
      }))

      let startIndex = 0

      while (startIndex < normalizedRows.length) {
        const currentRow = normalizedRows[startIndex]

        if (!currentRow) {
          startIndex += 1
          continue
        }

        const groupKey = [
          currentRow.item_name,
          currentRow.unit_name,
          currentRow.total_qty,
          currentRow.total_value,
          currentRow.average_days,
        ].join('|')

        let endIndex = startIndex + 1

        while (endIndex < normalizedRows.length) {
          const nextRow = normalizedRows[endIndex]

          if (!nextRow) {
            break
          }

          const nextGroupKey = [
            nextRow.item_name,
            nextRow.unit_name,
            nextRow.total_qty,
            nextRow.total_value,
            nextRow.average_days,
          ].join('|')

          if (nextGroupKey !== groupKey) {
            break
          }

          endIndex += 1
        }

        const rowSpan = endIndex - startIndex
        normalizedRows[startIndex].pdf_item_name_row_span = rowSpan
        normalizedRows[startIndex].pdf_unit_name_row_span = rowSpan
        normalizedRows[startIndex].pdf_total_qty_row_span = rowSpan
        normalizedRows[startIndex].pdf_total_value_row_span = rowSpan
        normalizedRows[startIndex].pdf_average_days_row_span = rowSpan

        for (let index = startIndex + 1; index < endIndex; index += 1) {
          normalizedRows[index].pdf_item_name_row_span = 0
          normalizedRows[index].pdf_unit_name_row_span = 0
          normalizedRows[index].pdf_total_qty_row_span = 0
          normalizedRows[index].pdf_total_value_row_span = 0
          normalizedRows[index].pdf_average_days_row_span = 0
        }

        startIndex = endIndex
      }

      return normalizedRows
    }

    const buildPdfBrandSections = () => {
      const brandSectionMap = new Map()
      let grandTotal = null

      stockData.forEach((row) => {
        if (row?.row_type === 'grand-total') {
          grandTotal = row
          return
        }

        if (row?.row_type === 'brand-header') {
          return
        }

        const brandLabel = row?.row_type === 'brand-total'
          ? String(row.brand_label || '').trim() || 'Unbranded'
          : normalizeBrandName(row)

        if (!brandSectionMap.has(brandLabel)) {
          brandSectionMap.set(brandLabel, {
            brandLabel,
            detailRows: [],
            totalRow: null,
          })
        }

        const section = brandSectionMap.get(brandLabel)

        if (row?.row_type === 'brand-total') {
          section.totalRow = row
          return
        }

        section.detailRows.push(row)
      })

      return {
        brandSections: Array.from(brandSectionMap.values())
          .map((section) => ({
            ...section,
            detailRows: [...section.detailRows].sort((firstRow, secondRow) => {
              const averageDaysComparison = toNumber(secondRow.average_days) - toNumber(firstRow.average_days)
              if (averageDaysComparison !== 0) {
                return averageDaysComparison
              }

              const itemNameComparison = compareText(firstRow.item_name, secondRow.item_name)
              if (itemNameComparison !== 0) {
                return itemNameComparison
              }

              const unitComparison = compareText(firstRow.unit_name, secondRow.unit_name)
              if (unitComparison !== 0) {
                return unitComparison
              }

              return toNumber(firstRow.days_in_inventory) - toNumber(secondRow.days_in_inventory)
            }),
          }))
          .filter((section) => section.detailRows.length > 0)
          .sort((firstSection, secondSection) => {
            const firstTotalValue =
              toNumber(firstSection.totalRow?.total_value) ||
              toNumber(firstSection.totalRow?.value) ||
              firstSection.detailRows.reduce((sum, row) => sum + toNumber(row.value), 0)
            const secondTotalValue =
              toNumber(secondSection.totalRow?.total_value) ||
              toNumber(secondSection.totalRow?.value) ||
              secondSection.detailRows.reduce((sum, row) => sum + toNumber(row.value), 0)

            if (secondTotalValue !== firstTotalValue) {
              return secondTotalValue - firstTotalValue
            }

            return compareText(firstSection.brandLabel, secondSection.brandLabel)
          }),
        grandTotalRow: grandTotal,
      }
    }

    const buildTableHooks = (pdfRows) => ({
      didParseCell: (data) => {
        if (data.row.section === 'head') {
          data.cell.styles.lineWidth = { bottom: 0.6 }
          data.cell.styles.lineColor = [0, 0, 0]

          if ([0, 1].includes(data.column.index)) {
            data.cell.styles.valign = 'middle'
          }

          if ([2, 3, 4, 5, 6, 7].includes(data.column.index)) {
            data.cell.styles.halign = 'right'
          }

        }

        const pdfRow = pdfRows[data.row.index]
        if (pdfRow?.rowType === 'detail' && pdfRow?.isItemGroupEnd) {
          data.cell.styles.lineWidth = { bottom: 0.4 }
          data.cell.styles.lineColor = [0, 0, 0]
        }

        if (pdfRow?.rowType === 'detail' && [0, 1].includes(data.column.index)) {
          data.cell.styles.valign = 'middle'
          if (data.cell.raw === ' ') {
            data.cell.text = ['']
          }
        }

        if (
          ['body'].includes(data.row.section) &&
          [6, 7].includes(data.column.index)
        ) {
          data.cell.styles.textColor = [11, 61, 145]
        }

        if (data.row.section === 'body' && data.column.index === 4) {
          data.cell.styles.textColor = [22, 101, 52]
          data.cell.styles.fontStyle = 'bold'
        }

        if (data.row.section === 'body' && data.column.index === 2) {
          data.cell.styles.textColor = [11, 61, 145]
          data.cell.styles.fontStyle = 'bold'
        }

        if (pdfRow?.rowType === 'detail' && data.cell.raw?.rowSpan > 1) {
          data.cell.styles.valign = 'middle'
          data.cell.styles.lineWidth = { bottom: 0.4 }
          data.cell.styles.lineColor = [0, 0, 0]
        }

        if (pdfRow?.rowType === 'grand-total') {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.lineWidth = { top: 0.3, bottom: 0.6 }
          data.cell.styles.lineColor = [0, 0, 0]
        }

        if (pdfRow?.rowType === 'brand-total') {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.fillColor = [245, 247, 250]
          data.cell.styles.lineWidth = { top: 0.2, bottom: 0.4 }
          data.cell.styles.lineColor = [160, 174, 192]
        }
      },
      didDrawCell: (data) => {
        const pdfRow = pdfRows[data.row.index]

        if (data.row.section !== 'body' || pdfRow?.rowType !== 'detail') {
          return
        }

        if (![0, 1].includes(data.column.index) || data.cell.raw !== ' ' || !pdfRow.itemGroupRowSpan) {
          return
        }

        const totalHeight = data.cell.height * pdfRow.itemGroupRowSpan
        const x = data.cell.x + 1.5
        const y = data.cell.y + totalHeight / 2 + 0.5

        doc.setFontSize(9)
        doc.setFont('Helvetica', 'normal')

        if (data.column.index === 1 && pdfRow.unitText) {
          doc.setTextColor(0, 0, 0)
          doc.text(pdfRow.unitText, x, y, { baseline: 'middle' })
          return
        }

        if (data.column.index !== 0 || !pdfRow.itemNameText) {
          return
        }

        doc.setTextColor(0, 0, 0)
        doc.text(pdfRow.itemNameText, x, y, { baseline: 'middle' })
      },
    })

    let currentY = 40
    const getPageHeight = () => {
      const currentPageSize = doc.internal.pageSize
      return currentPageSize.height ? currentPageSize.height : currentPageSize.getHeight()
    }

    const ensureSpace = (requiredHeight) => {
      const pageHeight = getPageHeight()
      if (currentY + requiredHeight > pageHeight - 18) {
        doc.addPage(pageFormat, detailOrientation)
        currentY = 40
      }
    }

    const { brandSections, grandTotalRow } = buildPdfBrandSections()
    const summaryRows = buildPdfSummaryRows(brandSections, grandTotalRow)

    doc.setFontSize(11)
    doc.setFont('Helvetica', 'bold')
    doc.setTextColor(11, 61, 145)
    doc.text('Brands Summary', 12, 40)

    const pageWidth = doc.internal.pageSize.width ? doc.internal.pageSize.width : doc.internal.pageSize.getWidth()
    const summaryTableWidth = pageWidth * 0.8
    const summaryLeftMargin = (pageWidth - summaryTableWidth) / 2

    doc.autoTable({
      theme: 'plain',
      startY: 44,
      head: [['Sr', 'Brand Name', 'Value of Stock', 'Average Days']],
      body: summaryRows,
      tableWidth: summaryTableWidth,
      styles: {
        fontSize: 9,
        cellPadding: 1.5,
        textColor: [0, 0, 0],
        valign: 'middle',
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [11, 61, 145],
        fontStyle: 'bold',
        fontSize: 9,
        valign: 'middle',
      },
      columnStyles: {
        0: { cellWidth: summaryTableWidth * 0.08, halign: 'center' },
        1: { cellWidth: summaryTableWidth * 0.42, halign: 'left' },
        2: { cellWidth: summaryTableWidth * 0.28, halign: 'right' },
        3: { cellWidth: summaryTableWidth * 0.22, halign: 'right' },
      },
      margin: {
        top: 40,
        bottom: 15,
        left: summaryLeftMargin,
        right: summaryLeftMargin,
      },
      didParseCell: (data) => {
        if (data.row.section === 'head') {
          data.cell.styles.lineWidth = { bottom: 0.6 }
          data.cell.styles.lineColor = [0, 0, 0]
          data.cell.styles.fontStyle = 'bold'
        }

        if (data.row.section === 'body' && data.row.index === summaryRows.length - 1 && grandTotalRow) {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.lineWidth = { top: 0.3, bottom: 0.6 }
          data.cell.styles.lineColor = [0, 0, 0]
        }

        if (data.column.index === 1) {
          data.cell.styles.halign = 'left'
        }

        if ([2, 3].includes(data.column.index)) {
          data.cell.styles.halign = 'right'
        }
      },
      didDrawPage: () => {
        header()
        footer()
      },
    })

    if (brandSections.length > 0) {
      doc.addPage(pageFormat, detailOrientation)
      currentY = 40
    }

    brandSections.forEach((section) => {
      const mergedDetailRows = withPdfMergedRows(section.detailRows)
      const pdfRows = mergedDetailRows.map((row, index) =>
        buildPdfDetailRow(row, mergedDetailRows[index + 1]),
      )

      if (section.totalRow) {
        pdfRows.push(buildPdfBrandTotalRow(section.totalRow))
      }

      if (!section.brandLabel || pdfRows.length === 0) {
        return
      }

      ensureSpace(20)
      const brandLabelY = currentY + 8
      doc.setFontSize(11)
      doc.setFont('Helvetica', 'bold')
      doc.setTextColor(22, 101, 52)
      doc.text(`Brand: ${section.brandLabel}`, 12, brandLabelY)

      doc.autoTable({
        ...tableOptions,
        ...buildTableHooks(pdfRows),
        startY: brandLabelY + 3,
        head: tableHead,
        body: pdfRows.map((row) => row.cells),
      })
      currentY = doc.lastAutoTable.finalY
    })

    if (grandTotalRow) {
      const pdfGrandTotalRows = [buildPdfGrandTotalRow(grandTotalRow)]
      ensureSpace(16)
      doc.autoTable({
        ...tableOptions,
        ...buildTableHooks(pdfGrandTotalRows),
        startY: currentY + 8,
        head: tableHead,
        body: pdfGrandTotalRows.map((row) => row.cells),
      })
      currentY = doc.lastAutoTable.finalY
    }

    const finalPageNumber = doc.internal.getNumberOfPages()
    doc.setPage(finalPageNumber)
    {
      const pageSize = doc.internal.pageSize
      const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight()

      doc.setFontSize(10)
      doc.setTextColor('black')
      doc.setFont('Helvetica', 'normal')
      doc.text('This report is printed through ERP at ', 60, pageHeight - 15)

      doc.setTextColor('#0202f7')
      doc.setFont('Helvetica', 'bold')
      doc.text(currentDateTime(), 124, pageHeight - 15)
    }

    if (typeof doc.putTotalPages === 'function') {
      doc.putTotalPages(totalPagesExp)
    }

    const pdfBlobUrl = doc.output('bloburl')
    window.open(pdfBlobUrl, '_blank')
  }

  useEffect(() => {
    wtApiCall(
      'dropdown',
      {
        dropdown_names: 'warehouses,items,warehouse_locations_with_permissions,warehouse_locations',
      },
      'get',
      (result) => {
        const warehousesData = result?.data?.[0]?.warehouses || []
        const itemsData = result?.data?.[1]?.items || []
        const warehouseLocationsWithPermissions =
          result?.data?.[2]?.warehouse_locations_with_permissions || []
        const warehouseLocations = result?.data?.[3]?.warehouse_locations || []
        const resolvedWarehouseLocations =
          warehouseLocationsWithPermissions.length > 0
            ? warehouseLocationsWithPermissions
            : warehouseLocations

        setAllWarehouses(warehousesData)
        setItems(itemsData)
        setWarehouseLocations(resolvedWarehouseLocations)
        setWarehouseLocationIds([])
        setWarehouses(warehousesData)
        syncSelectedWarehouse(warehousesData)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setStockData([])
      },
    )
  }, [])

  const reportContent = (
    <>
        <Row gutter={[24, 24]}>
          <Col xs={12} sm={8} md={3}>
            <label>As Of: *</label>
            <WidgetDatePicker value={dateRange} setDate={setDateRange} />
          </Col>

          <Col xs={12} sm={8} md={4}>
            <div className="form-group">
              <label>Warehouse</label>
              <WidgetWarehouseSelector
                warehouses={warehouses}
                onChange={(values) => changeValues('warehouse_id', values)}
                displayValue={WarehouseName}
                reloadDropdownData={() => Base.dropdownData('warehouses', {}, [setWarehouses], 1)}
                value={WarehouseId}
              />
            </div>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <div className="form-group">
              <label>Item</label>
              <WidgetItemSelector
                items={Items}
                onChange={(values) => changeValues('item_id', values)}
                displayValue={ItemName}
                reloadDropdownData={() => Base.dropdownData('items', {}, [setItems], 1)}
                placeHolder={'Select Item'}
                value={ItemId}
              />
            </div>
          </Col>
          <Col xs={12} sm={8} md={5}>
            <div className="form-group">
              <label>Warehouse Location *</label>
              <Select
                mode="multiple"
                maxTagCount="responsive"
                options={WarehouseLocations}
                value={WarehouseLocationIds}
                onChange={(values) => changeValues('warehouse_location_ids', values)}
                placeholder="Select Location"
                style={{ width: '100%' }}
                status={WarehouseLocationIds.length === 0 ? 'error' : ''}
              />
            </div>
          </Col>
          <Col
            xs={24}
            sm={24}
            md={8}
            style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', flexWrap: 'wrap' }}
          >
            <Button
              style={{ marginTop: 30 }}
              type="primary"
              onClick={handleGenerateReport}
              disabled={isLoading}
            >
              Generate Report
            </Button>
            <Button
              className="text-white"
              style={{
                marginTop: 30,
                backgroundColor: '#28A745',
              }}
              onClick={onDownload}
              icon={<FileExcelOutlined />}
            />
            <Button
              style={{ marginTop: 30, backgroundColor: '#FF0000', color: '#fff' }}
              onClick={generatePDF}
              icon={<FilePdfOutlined />}
            />
          </Col>
        </Row>

        <br />
        <div style={{ width: '100%' }}>
          <table className="table table-bordered data-table" ref={tableRef}>
            <Table
              columns={columns}
              dataSource={tableData}
              pagination={false}
              bordered
              size="small"
              rowKey={(record, index) => index}
              loading={{ indicator: <Spin />, spinning: isLoading }}
              rowClassName={(record) =>
                record.row_type === 'grand-total'
                  ? 'total-row'
                  : record.row_type === 'brand-total'
                    ? 'brand-total-row'
                    : record.row_color_class || ''
              }
            />
          </table>
        </div>

        <Modal
          title={
            detailsModalType === 'average'
              ? 'Average Days Verification'
              : 'Days in Inventory Verification'
          }
          open={!!selectedInventoryRow}
          onCancel={closeInventoryDetails}
          footer={null}
          width={1100}
        >
          {selectedInventoryRow && (
            <>
              <div>
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(record, index) =>
                    `${record.reference_type || 'ref'}-${record.reference_number || 'num'}-${record.reference_date || 'date'}-${index}`
                  }
                  dataSource={
                    detailsModalType === 'average'
                      ? averageDaysBreakdown.flatMap((row) => row.detail_breakdown || [])
                      : selectedInventoryRow.detail_breakdown || []
                  }
                  columns={[
                    {
                      title: 'Reference Type',
                      dataIndex: 'reference_type',
                      key: 'reference_type',
                      render: (value, record) => {
                        if (value === 'GRN' && record?.grn_id) {
                          return (
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0, height: 'auto' }}
                              onClick={() => window.open(`/goods_recevied_note#view:${record.grn_id}`, '_blank')}
                            >
                              {value}
                            </Button>
                          )
                        }

                        return value || ''
                      },
                    },
                    {
                      title: 'Reference Number',
                      dataIndex: 'reference_number',
                      key: 'reference_number',
                      render: (value) => value || '',
                    },
                    {
                      title: 'Reference Date',
                      dataIndex: 'reference_date',
                      key: 'reference_date',
                      render: (value) => (value ? moment(value).format('YYYY-MM-DD') : ''),
                    },
                    {
                      title: 'GRN ID',
                      dataIndex: 'grn_id',
                      key: 'grn_id',
                      render: (value) => value || '',
                    },
                    {
                      title: 'GRN Number',
                      dataIndex: 'grn_number',
                      key: 'grn_number',
                      render: (value) => value || '',
                    },
                    {
                      title: 'GRN Date',
                      dataIndex: 'grn_date',
                      key: 'grn_date',
                      render: (value) => (value ? moment(value).format('YYYY-MM-DD') : ''),
                    },
                    {
                      title: 'Qty',
                      dataIndex: 'qty',
                      key: 'qty',
                      align: 'right',
                      render: (value) => wtFormatRoundedValue(value, '', false, 0),
                    },
                    {
                      title: 'Days in Inventory',
                      dataIndex: 'days_in_inventory',
                      key: 'days_in_inventory',
                      align: 'right',
                      render: (value) => wtRoundValue(value, 0),
                    },
                    {
                      title: 'Value',
                      dataIndex: 'value',
                      key: 'value',
                      align: 'right',
                      render: (value) => wtFormatRoundedValue(value, '', false, 3),
                    },
                    ...(detailsModalType === 'average'
                      ? [
                          {
                            title: 'Qty x Days',
                            key: 'qty_x_days',
                            align: 'right',
                            render: (_, record) =>
                              wtRoundValue((+record.qty || 0) * (+record.days_in_inventory || 0), 0),
                          },
                        ]
                      : []),
                  ]}
                  summary={() => {
                    const modalRows =
                      detailsModalType === 'average'
                        ? averageDaysBreakdown.flatMap((row) => row.detail_breakdown || [])
                        : selectedInventoryRow.detail_breakdown || []
                    const totalQty = modalRows.reduce(
                      (sum, row) => sum + (+row.qty || 0),
                      0,
                    )
                    const totalValue =
                      detailsModalType === 'average'
                        ? averageDaysBreakdown.reduce((sum, row) => sum + (+row.value || 0), 0)
                        : +selectedInventoryRow.value || 0
                    const totalQtyDays = modalRows.reduce(
                      (sum, row) => sum + (+row.qty || 0) * (+row.days_in_inventory || 0),
                      0,
                    )

                    return (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>
                          <strong>Total</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1}></Table.Summary.Cell>
                        <Table.Summary.Cell index={2}></Table.Summary.Cell>
                        <Table.Summary.Cell index={3}></Table.Summary.Cell>
                        <Table.Summary.Cell index={4}></Table.Summary.Cell>
                        <Table.Summary.Cell index={5}></Table.Summary.Cell>
                        <Table.Summary.Cell index={6} align="right">
                          <strong>{wtFormatRoundedValue(totalQty, '', false, 0)}</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={7} align="right">
                          <strong></strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={8} align="right">
                          <strong>{wtFormatRoundedValue(totalValue, '', false, 3)}</strong>
                        </Table.Summary.Cell>
                        {detailsModalType === 'average' && (
                          <Table.Summary.Cell index={9} align="right">
                            <strong>{wtRoundValue(totalQtyDays, 0)}</strong>
                          </Table.Summary.Cell>
                        )}
                      </Table.Summary.Row>
                    )
                  }}
                  locale={{ emptyText: 'No backtracking details available for this row' }}
                />
              </div>
            </>
          )}
        </Modal>
    </>
  )

  if (isEmbedded) {
    return reportContent
  }

  return (
    <div
      className="container-fluid"
      style={{
        padding: '1em',
        backgroundColor: 'white',
        minHeight: '100vh',
        fontFamily: 'Helvetica',
      }}
    >
      <Card
        title={
          <div className="stock-aging-report-title-block">
            <div className="stock-aging-report-version-tabs">
              <ReportVersionTabs activeKey="stock-aging-report-v2" />
            </div>
            <span className="stock-aging-report-title">{companyName + ' Stock Aging Report V2'}</span>
          </div>
        }
        style={{ width: '100%' }}
      >
        {reportContent}
      </Card>
    </div>
  )
}

export default StockAgingReportV2
